import RichText from '@eneco/components/RichText/RichText';
import { PaymentMethodDto, PaymentPlanInstallmentCardResponse } from '@eneco/dc-be/client';
import { useFormatter } from '@eneco/i18n';
import { Stack } from '@eneco/sparky';
import { EnergyBill } from '@eneco/sparky/illustrations';
import { useContent } from '@sitecore/common';
import { DashboardOverviewRendering } from '@sitecore/types/DashboardOverview';
import { isBefore } from 'date-fns';

import { StyledDashboardBlock } from './DashboardBlock';
import { DashboardLink } from './DashboardLink';

const getPaymentMethodDescription = (
  paymentMethod: PaymentMethodDto | undefined,
  hasOverdueSlices: boolean | undefined,
  fields: DashboardOverviewRendering['fields'],
) => {
  if (hasOverdueSlices) {
    return fields.paymentPlanInstallmentCard?.overdueDescription.value || 'Overdue [not in Sitecore] {amount}';
  }
  switch (paymentMethod) {
    case 'DirectDebit':
      return (
        fields.paymentPlanInstallmentCard?.directDebitDescription.value || 'Direct debit [not in Sitecore] {amount}'
      );
    case 'DirectDebitExcludingYearlyBill':
      return (
        fields.paymentPlanInstallmentCard?.directDebitExcludingYearlyBillDescription.value ||
        'Direct debit excluding yearly bill [not in Sitecore] {amount}'
      );
    case 'BankTransfer':
      return (
        fields.paymentPlanInstallmentCard?.bankTransferDescription.value || 'Bank transfer [not in Sitecore] {amount}'
      );
  }
};

export const PaymentPlanInstallmentCard = ({ card }: { card: PaymentPlanInstallmentCardResponse }) => {
  const { fields } = useContent<DashboardOverviewRendering>();

  const {
    details: { paymentPlan },
  } = card;
  const { format, currency, date } = useFormatter();

  const { paymentMethod } = paymentPlan || {};
  const today = new Date();

  const hasOverdueSlices = paymentPlan?.slices.some(slice => !slice.paid && isBefore(new Date(slice.end), today));
  const firstUnpaidSlice = paymentPlan?.slices.find(slice => !slice.paid);

  return (
    <StyledDashboardBlock>
      <Stack direction="row" gap="4" alignY="center">
        <Stack.Item shrink={false}>
          <EnergyBill />
        </Stack.Item>
        <div>
          <RichText
            html={format(getPaymentMethodDescription(paymentMethod, hasOverdueSlices, fields) || '', {
              amount: currency.euro(firstUnpaidSlice?.amount || 0),
              date: firstUnpaidSlice?.end ? date.short(new Date(firstUnpaidSlice.end)) : '',
            })}
          />
        </div>
      </Stack>
      <DashboardLink
        href={fields.paymentPlanInstallmentCard?.link.value.href}
        emphasis="high"
        trackingComponentName="payment_plan_installment_link"
        trackingComponentType="dashboard_link">
        {fields.paymentPlanInstallmentCard?.link.value.text || 'Payment plan [not in Sitecore]'}
      </DashboardLink>
    </StyledDashboardBlock>
  );
};
